// First-party side panels (workspace-modes note §11.5). Slice 1 ships the slot and one working
// panel, "Details", which renders the shared playhead and respects the kpi entitlement. M7-04:
// the Memory panel (feature `memory`) shows what Recall knows about the focused subject.
import { Badge } from "@companyos/ui";
import type { MeResponse } from "../api/types";
import { ThreadSidePanel } from "../features/chat/thread-side-panel";
import type { CompanyAccess } from "../features/company";
import { MemoryPanel } from "../features/memory/memory-panel";
import { featureState } from "../features/use-feature";
import { extensionPoints } from "./modes";
import type { PanelEntry, PanelProps } from "./registry";

const SUBJECT_LABEL = {
  seat: "Seat",
  person: "Person",
  task: "Task",
  de: "Digital employee",
  team: "Team",
};

function DetailsPanel({ focus, feature }: PanelProps) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm" data-panel="details">
      <dt className="text-muted-foreground">Subject</dt>
      <dd>
        {focus.subject
          ? `${SUBJECT_LABEL[focus.subject.kind]}: ${focus.subject.id}`
          : "Nothing selected"}
      </dd>
      <dt className="text-muted-foreground">Period</dt>
      <dd>{focus.period ?? "This week"}</dd>
      <dt className="text-muted-foreground">Scope</dt>
      <dd>{focus.scope === "company" ? "Company" : focus.scope}</dd>
      {feature.readOnly ? (
        <dd className="col-span-2">
          <Badge className="bg-nav-read-only text-nav-read-only-foreground">Read-only</Badge>
        </dd>
      ) : null}
    </dl>
  );
}

extensionPoints
  .register("panel", {
    id: "panel:details",
    publisher: "companyos",
    key: "details",
    label: "Details",
    feature: "kpi",
    // UX-B: and Workforce, where a DE's scorecard now lives (its page's KPIs tab).
    modes: ["kpis", "reports", "agents"],
    order: 1,
    component: DetailsPanel,
  })
  .register("panel", {
    id: "panel:memory",
    publisher: "companyos",
    key: "memory",
    label: "Memory",
    // CHAT-PANEL: beside a conversation it shows what Recall holds about that conversation.
    labelIn: { chat: "Related memory" },
    // Its search box and links stay put; only the list scrolls.
    flush: true,
    feature: "memory",
    modes: "all",
    order: 2,
    component: MemoryPanel,
  })
  .register("panel", {
    // Chat (M2-06; spec 06): a message's thread opens here, beside the conversation.
    id: "panel:thread",
    publisher: "companyos",
    key: "thread",
    label: "Thread",
    feature: "chat",
    modes: ["chat"],
    order: 0,
    // Its reply list scrolls on its own; the composer stays at the bottom.
    flush: true,
    component: ThreadSidePanel,
  });

/** Panels for this mode whose add-on is enabled, in tab order. */
export function visiblePanels(
  me: MeResponse,
  company: CompanyAccess,
  modeKey: string | null,
): PanelEntry[] {
  if (!modeKey) return [];
  return extensionPoints
    .list("panel")
    .filter((p) => p.modes === "all" || p.modes.includes(modeKey))
    .filter((p) => featureState(me, company.id, p.feature).enabled)
    .sort((a, b) => a.order - b.order);
}
